import { SettingService } from '../../../core/service/setting.service';
import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, ElementRef, ViewChild } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TaskService } from 'src/app/core/service/task.service';
import { Guid } from 'guid-typescript';
import { sortBy, cloneDeep } from 'lodash';
import { AssessmentListService } from 'src/app/core/service/grid-data/grid-data-assessment.service';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UserService } from 'src/app/core/service/user.service';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { CustomFile } from 'src/app/message/model/custom-file';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { DocumentService } from 'src/app/core/service/document.service';
import { DocumentComponent } from 'src/app/document/document.component';

@Component({
  selector: 'app-formly-designer-task-edit',
  templateUrl: './formly-designer-task-edit.component.html',
  styleUrls: ['./formly-designer-task-edit.component.scss'],
  // Creating another instances of the AssessmentListService to prevent the list to changed for deleted Assessments
  providers: [AssessmentListService]
})
export class FormlyDesignerTaskEditComponent implements OnInit, OnDestroy
{
  @Input() public assessmentId = 0;
  @Input() public fields: FormlyFieldConfig[];
  @Input() public isDeleted = false;

  @Output() public formChanged: EventEmitter<any> = new EventEmitter();

  @ViewChild('documentComponent') public documentComponent: DocumentComponent;
  @ViewChild('documentComponentViewer') public documentComponentViewer: DocumentComponent;

  public editForm: any;
  public tasks: UntypedFormArray;
  public listPriorities: Array<{ id: string; text: string; value: number }>;
  public assessments: any;
  public assessmentsFiltered = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;
  public loaded = false;

  public selectedFiles: CustomFile[] = [];
  public fileRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.pdf', '.doc', '.docx'],
      maxFileSize: 2000
    };

  private listSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private taskService: TaskService,
    private settingService: SettingService,
    private assessmentListService: AssessmentListService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private messageHandlerService: MessageHandlerService,
    private elem: ElementRef,
    private documentService: DocumentService,
  )
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (data) =>
      {
        const priorities = data.taskPriorities.split(',');
        this.listPriorities = this.taskService.listPriorities.filter(priority => priorities.includes(priority.id));
        this.listPriorities.sort((a, b) => (a.value > b.value) ? -1 : 1);
      }
    });
    this.getAssessments();
  }

  @Input() public set model(tasks: any)
  {
    this.initModel(tasks);
  }

  public ngOnInit(): void
  {
    this.loaded = false;
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Add a task entry
   */
  public addTask(id: number = null, version = 0, description = '', note = '', name = '',
    taskAssessment: any = null, dueDateDays: number = null, priority: any = null, query: any = null, showRules = true,
    importError = false): void
  {
    this.editForm.controls.tasks.push(this.initTask(id, version, description, note, name,
      taskAssessment, dueDateDays, priority, query, showRules, importError));
    if (id === null)
    {
      this.editForm.markAsDirty();
    }
  }

  /**
   * Remove a task entry
   *
   * @param i Index
   */
  public removeTask(i: number): void
  {
    const control = this.editForm.controls.tasks;
    control.removeAt(i);
    this.editForm.markAsDirty();
  }

  public showRulesChanged(e: any): void
  {
    this.editForm.controls.tasks.controls[e.index].controls.showRules.value = e.show;
    // If the rule is opened, we assumed a state changes.
    if (!this.isDeleted)
    {
      this.editForm.markAsDirty();
      this.formChanged.emit(this.editForm);
    }
  }

  /**
   * Used for the trackBy to prevent rendering the DOM and losing field focus.
   *
   * @param index Row index.
   * @param item Row item.
   */
  public tasksTrackBy(index: number, item: any): string
  {
    return item.value.guid;
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public assessmentItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Filter handler for assessment dropdown.
   *
   * @param value User input.
   */
  public assessmentFilter(value: string): void
  {
    this.searchTerm = value;
    this.assessmentsFiltered = sortBy(this.assessments.filter(p => p.name.toLowerCase().includes(value.toLowerCase())), ['name']);
  }

  /**
 * File has been selected.
 *
 * @param event Event
 */
  public onFileChanged(event: UploadEvent): void
  {
    event.files.forEach((file) =>
    {
      if (this.selectedFiles.find(f => f.name === file.name) !== undefined)
      {
        this.messageHandlerService.displayError('FILE-ALREADY-ATTACHED');
        return;
      }

      // Validate file size
      if ((file.size / 1024) > this.fileRestrictions.maxFileSize)
      {
        this.messageHandlerService.displayError('FILE-SIZE-EXCEED-LIMIT');
        return;
      }

      // Validate file extension
      /*
      let validExtension = false;
      this.fileRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });*/

      this.editForm.markAsDirty();
      const customFile = new CustomFile();
      customFile.name = file.name;
      customFile.type = file.rawFile.type;
      this.setFileFromBlob(customFile, file.rawFile);
      this.selectedFiles.push(customFile);
    });
  }

  public deleteAttachment(name: string): void
  {
    this.selectedFiles = this.selectedFiles.filter(f => f.name !== name);
  }

  /**
   * Select attachment and open the uploader.
   */
  public selectAttachment(): void
  {
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  /**
 * Return the proper class to used based on the file mime type.
 *
 * @param mimeType Mime Type of the file.
 * @returns String
 */
  public getFileTypeClass(mimeType: string): string
  {
    return this.documentService.getFileTypeClass(mimeType);
  }

  /**
 * View document handler
 *
 * @param url Document Url
 * @param description Document description
 * @param mediaType Media mime type
 * @param createdOn Document created date
 */
  public viewHandler(url: string, description: string, mediaType: string, createdOn: string): void
  {
    this.documentComponentViewer.viewHandler(
      url,
      description,
      mediaType,
      createdOn
    );
  }

  /**
 * Set the selected file.
 *
 * @param file Selected file.
 */
  private setFileFromBlob(customFile: CustomFile, file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      const base64: any = reader.result;
      customFile.data = base64.split('base64,')[1];
    }, false);
    reader.readAsDataURL(file);
  }

  /**
   * Get all assessments
   */
  private getAssessments(): void
  {
    if (this.listSubscription === undefined)
    {
      this.listSubscription = this.assessmentListService.subscribe({
        next: (value) =>
        {
          if (value !== null)
          {
            let assessments = cloneDeep(value.data);
            assessments.forEach(d => { d.name = d.name + (d.assessmentVersion ? ' - v' + d.assessmentVersion : ''); });

            // Prevent circular reference and remove current assessment id from available assessements
            if (this.assessmentId !== null)
            {
              assessments = assessments.filter(x => x.id !== this.assessmentId);
            }
            assessments = assessments.filter(x => !x.type.consentType);
            this.assessments = assessments;
            this.assessmentFilter('');
          }
        }
      });
    }

    const state = {
      filter: {
        filters: [{ field: 'deleted', operator: 'eq', value: false }], logic: 'and'
      },
      group: [],
      skip: 0,
      sort: [{ field: 'name', dir: 'asc' }],
      take: 99999
    };
    this.assessmentListService.queryByState(state);
  }

  /**
   * Disable controls when Assessement is deleted
   */
  private disableControls(): void
  {
    if (this.isDeleted)
    {
      this.editForm.controls.tasks.controls.forEach(data =>
      {
        data.controls.description.disable();
        data.controls.name.disable();
        data.controls.taskAssessment.disable();
        data.controls.dueDateDays.disable();
        data.controls.priority.disable();
      });
    }
  }

  /**
   * Model initilization
   *
   * @param tasks Assessment tasks edit object.
   */
  private initModel(tasks: any): void
  {
    if (tasks === undefined || this.loaded)
    {
      return;
    }

    // Build form
    this.editForm = this.fb.group({
      tasks: this.fb.array([])
    });

    if (tasks && tasks.length > 0)
    {
      tasks.forEach(data =>
      {
        this.addTask(data.id, data.version, data.description, data.note, data.name,
          data.taskAssessment, data.dueDateDays, data.priority, data.query, false, data.importError);
      });      
    }

    this.editForm.valueChanges.subscribe({
      next: () =>
      {
        if (!this.isDeleted)
        {
          this.formChanged.emit(this.editForm);
        }
      }
    });

    // Must apply a delay here because isDeleted is not set yet when setting the model.
    setTimeout(() =>
    {
      this.disableControls();
      this.loaded = true;
    });
  }

  /**
   * Create a form group.
   */
  private initTask(id: number, version: number, description: string, note: string, name: string,
    taskAssessment: any, dueDateDays: number, priority: any, query: any, showRules: boolean, importError: boolean): UntypedFormGroup
  {
    if (query === null)
    {
      query = {
        condition: 'and',
        rules: []
      };
    }
    return this.fb.group({
      guid: Guid.create().toString(),
      id: [id],
      version: [version],
      description: [description],
      note: [note],
      name: [name, Validators.required],
      taskAssessment: [taskAssessment],
      dueDateDays: [dueDateDays],
      priority: [priority, Validators.required],
      query: [query, Validators.required],
      showRules: [showRules],
      importError: [importError]
    });
  }
}
